import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';

import { AdminContext } from '../contexts/admin.context';
import { CreateProjectSchema } from '../schema/project.schema';
import { createModerator, createProject, getModerators } from '../service';
import { getUserId } from '../utils/user';

import Button from '../components/common/Button';
import AdminInput from '../components/common/AdminInput';
import NumberInput from '../components/common/NumberInput';
import { InfoIcon, RocketIcon } from '../components/icons';
import { trackEvent } from '../utils';
import { getTranslation } from '../utils/translation';
import InputSelect from '../components/common/InputSelect';
import { ModeratorForm } from '../components/forms';
import { POPUP_DEFAULT_VALUES } from '../data/default-values';

const ClassProject = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [creatingClass, setCreatingClass] = useState(false);
  const [loadingModerators, setLoadingModerators] = useState(false);
  const [moderators, setModerators] = useState([]);

  const {
    setPopupInfo,
    setContentPopupInfo,
    themeId,
    teamType,
    user,
    portalConfig: { productId, projectTypes, languages = [], enableModerators } = {}
  } = useContext(AdminContext);

  const [languageOptions] = useState([
    { key: '', title: '' },
    ...languages?.map((l) => ({ value: l, label: l.toUpperCase() }))
  ]);
  const [filteredProjectTypes, setFilteredProjectTypes] = useState([]);

  const basePath = `/${themeId}/${teamType}`;

  const bottomDescription = getTranslation(
    t,
    'createProject.bottomDescription',
    false,
    0,
    false,
    true
  );

  const handleSubmit = async (formValues) => {
    // if (
    //   enableModerators &&
    //   user?.administrator &&
    //   (!formValues?.moderatorEmail || formValues?.moderatorEmail === 'addNewMod')
    // ) {
    //   formik.setFieldError(
    //     'moderatorEmail',
    //     getTranslation(t, 'createProject.errors.moderatorEmailRequired')
    //   );
    //   toast.error(getTranslation(t, 'createProject.errors.moderatorEmailRequired'));
    //   return;
    // }

    const userAccountId = getUserId(user, themeId, teamType, 'account');
    const userId = getUserId(user, themeId, teamType);

    if (!formValues?.projectType) formValues.projectType = filteredProjectTypes?.[0].value;

    const projectTypeObject = filteredProjectTypes?.find((p) => p.value === formValues.projectType);

    const projectValues = {
      accountId: userAccountId,
      productId: projectTypeObject?.customVIPProductId || productId,
      noOfParticipants: formValues.noOfParticipants,
      specs: {
        moderator: formValues.moderatorEmail || userId,
        language: formValues.language,
        notTanProtected: false,
        description: formValues.classCodeName,
        projectType: formValues.projectType,
        teamType: teamType?.toLowerCase(),
        teacherRegion: user?.address?.[0]?.street
      }
    };

    try {
      setCreatingClass(true);
      const response = await createProject(projectValues);
      // show succcess popup
      if (response.status === 200) {
        setPopupInfo({
          isVisible: true,
          icon: RocketIcon,
          heading: getTranslation(t, 'createProject.manageYourHead'),
          description: getTranslation(t, 'createProject.yourProjectCreated'),
          action: {
            text: getTranslation(t, 'createProject.manageYourProject'),
            type: 'normal',
            onClick: () => {
              navigate(basePath + '/projects');
              setPopupInfo({ isVisible: false });
            }
          }
        });
        formik.resetForm();
        return;
      }
      toast.error(getTranslation(t, 'createProject.errors.failedCreateProject'));
    } catch (e) {
      toast.error(getTranslation(t, 'createProject.errors.failedCreateProject'));
      console.error('Filed to create a new project', e);
    } finally {
      formik.resetForm();
      setCreatingClass(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      noOfParticipants: 1,
      classCodeName: '',
      projectType: projectTypes?.find((p) => p.enabled)?.id || projectTypes?.[0]?.id,
      language: '',
      moderatorEmail: getUserId(user, themeId, teamType) || ''
    },
    validationSchema: CreateProjectSchema,
    onSubmit: handleSubmit
  });

  const fetchModerators = async () => {
    try {
      setLoadingModerators(true);
      const res = await getModerators();
      const addNewModOption = {
        value: 'addNewMod',
        label: getTranslation(t, 'createProject.form.addNewModerator')
      };
      setModerators([
        addNewModOption,
        ...res.data?.map((m) => ({
          value: m.email,
          label: `${m.firstName} ${m.lastName} (${m.email})`
        }))
      ]);
    } catch (e) {
      console.error('Filed to get moderators', e);
    } finally {
      setLoadingModerators(false);
    }
  };

  const handleModeratorFormSubmit = async (values) => {
    try {
      console.log('values', values, { user });
      await createModerator({ ...values, languageId: user?.languageId });
      fetchModerators();
      setContentPopupInfo(POPUP_DEFAULT_VALUES);
    } catch (e) {
      console.error('Filed to create moderator', e);
      const errorMessage =
        e?.response?.data?.message?.detail ||
        getTranslation(t, 'createProject.errors.failedCreateModerator');
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    if (projectTypes?.length > 0) {
      const filteredProjects = projectTypes
        ?.map(({ id, title, enabled, ...rest }) => ({
          ...rest,
          value: id,
          label: title?.[i18n.language] || title?.en || title?.de || title?.fr || title?.it,
          disabled: !enabled
        }))
        .filter((p) => !p.disabled);

      setFilteredProjectTypes(filteredProjects);

      if (formik) formik.setFieldValue('projectType', filteredProjects?.[0]?.value);
    }
  }, [projectTypes]);

  useEffect(() => {
    if (enableModerators) {
      fetchModerators();
    }
  }, [enableModerators]);

  useEffect(() => {
    trackEvent('page_view', {
      organization: themeId === 'ch' ? 'SSK' : 'WSF'
    });
  }, []);

  useEffect(() => {
    // Auto-select the only language if there's just one option
    if (languageOptions?.length < 2) {
      formik.setFieldValue('language', 'fr');
    } else if (languageOptions?.length < 3) {
      // length is 2 because we have an empty option + 1 language
      formik.setFieldValue('language', languageOptions[1].value);
    }
  }, [languageOptions]);

  return (
    <div className="overflow-y-auto w-full mini-scrollbar pr-4">
      <div className="mb-14">
        <h1
          className="text-2xl font-bold text-left text-cblack-primary mb-2.5"
          dangerouslySetInnerHTML={{ __html: getTranslation(t, 'createProject.listHead') }}
        />
        <div
          className="text=lg text-cblack-primary"
          dangerouslySetInnerHTML={{ __html: getTranslation(t, 'createProject.point1') }}
        />
      </div>

      <div className="mt-5 max-w-xl px-1">
        <form onSubmit={formik.handleSubmit}>
          <InputSelect
            label={getTranslation(t, 'createProject.form.projectType')}
            value={formik.values.projectType}
            options={filteredProjectTypes}
            formik={formik}
            onChange={(value) => {
              formik.setFieldValue('projectType', value);
            }}
            name="projectType"
          />
          <NumberInput
            label={getTranslation(t, 'createProject.form.noOfParticipantsLabel')}
            subtext={getTranslation(t, 'createProject.form.noOfParticipantsSubtext')}
            isDark={true}
            name="noOfParticipants"
            min="0"
            formik={formik}
          />
          {languageOptions?.length > 2 && (
            <InputSelect
              label={getTranslation(t, 'createProject.form.languageLabel')}
              value={formik.values.language}
              options={languageOptions}
              formik={formik}
              onChange={(value) => {
                formik.setFieldValue('language', value);
              }}
              name="language"
              error={formik.errors.language}
              touched={formik.touched.language}
            />
          )}
          <AdminInput
            label={getTranslation(t, 'createProject.form.classCodeNameLabel')}
            subtext={getTranslation(t, 'createProject.form.classCodeNameSubtext')}
            isDark={true}
            placeholder={getTranslation(t, 'createProject.form.classCodePlaceholder')}
            type="text"
            name="classCodeName"
            formik={formik}
          />
          {enableModerators && user?.administrator && (
            <InputSelect
              loading={loadingModerators}
              options={moderators}
              label={getTranslation(t, 'createProject.form.moderatorEmailLabel')}
              subtext={getTranslation(t, 'createProject.form.moderatorEmailSubtext')}
              isDark={true}
              placeholder={getTranslation(t, 'createProject.form.moderatorEmailPlaceholder')}
              type="text"
              name="moderatorEmail"
              error={formik.errors.moderatorEmail}
              touched={formik.touched.moderatorEmail}
              onChange={(value) => {
                if (value === 'addNewMod') {
                  setContentPopupInfo({
                    isVisible: true,
                    maxWidth: 'max-w-3xl',
                    content: (
                      <ModeratorForm
                        setContentPopupInfo={setContentPopupInfo}
                        onSubmit={handleModeratorFormSubmit}
                      />
                    )
                  });
                  return;
                }
                formik.setFieldValue('moderatorEmail', value);
              }}
            />
          )}
          <Button buttonType="login" type="submit" loading={creatingClass} disabled={creatingClass}>
            {getTranslation(t, 'createProject.actionCreate')}
          </Button>
        </form>
      </div>

      {(bottomDescription?.[themeId] || bottomDescription?.default) && (
        <div className="bg-cwhite-secondary text-gray-primary p-4 rounded-lg mt-5 flex gap-2 max-w-xl">
          <div className="mt-1">
            <InfoIcon />
          </div>
          <div
            dangerouslySetInnerHTML={{
              __html: bottomDescription?.[themeId]
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ClassProject;
